import restClient, { ApiError } from './RestClient';
import { EntityMapLayer, MapLayerDataType } from './entities/replancity_MapLayer';
import { MetadataPropertyEntity } from './entities/replancity_MetadataProperty';

const LAMBDA_SERVICE_PATH = 'services/replancity_LambdaOperationService';

export type LambdaTaskStatus =
    | 'NEVER'
    | 'OUTDATED'
    | 'IN_PROGRESS'
    | 'PREPARE_MODEL'
    | 'COMPLETE'
    | 'RESTARTING'
    | 'TERMINATING'
    | 'CANCELED'
    | 'ERROR'
    | 'QUEUE'
    | 'TIMEOUT'
    | string;

export type LambdaOperationFieldOption = {
    value: string | number | boolean;
    caption?: string;
    label?: string;
    title?: string;
    name?: string;
};

export type LambdaOperationField = {
    id?: string;
    name: string;
    key?: string;
    label?: string;
    title?: string;
    printedName?: string;
    type?: string;
    required?: boolean;
    defaultValue?: unknown;
    value?: unknown;
    options?: LambdaOperationFieldOption[] | Record<string, unknown>;
    min?: number;
    max?: number;
    step?: number;
    placeholder?: string;
    helpText?: string;
    description?: string;
    validationRegex?: string;
};

export type LambdaOperationMetadata = {
    title?: string;
    description?: string;
    fields: LambdaOperationField[];
};

export type LambdaOperation = {
    id?: string;
    operationId?: string;
    code?: string;
    type?: string;
    name?: string;
    title?: string;
    printedName?: string;
    description?: string;
    endpoint?: string;
    startEndpoint?: string;
    actionUrl?: string;
    metadata?: LambdaOperationMetadata | LambdaOperationField[] | string;
    metadataEndpoint?: string;
    metadataUrl?: string;
    metadataEntityName?: string;
    icon?: string;
} & Record<string, unknown>;

export type LambdaStartPayload = {
    metadata: string;
};

export type LambdaHistoryItem = {
    id: string;
    name?: string;
    type?: string;
    state?: LambdaTaskStatus;
    params?: string | Record<string, unknown>;
    createTs?: string;
    updateTs?: string;
    time?: string;
    lambdaLayerId?: string;
    layerEndpoint?: string;
    layerUrl?: string;
    containsDashboard?: boolean;
    isRestartable?: boolean;
    dashboardUrl?: string;
    dashboardLink?: string;
    dashboardPath?: string;
    dashboardId?: string;
    dashboard?: {
        id?: string;
        url?: string;
        link?: string;
        path?: string;
    };
} & Record<string, unknown>;

export type LambdaHistoryActionFlags = {
    containsDashboard?: boolean;
    isRestartable?: boolean;
};

export type LambdaLayerDescriptor = EntityMapLayer & Partial<MapLayerDataType>;

const isFeatureCollection = (value: unknown): value is GeoJSON.FeatureCollection => {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const record = value as Record<string, unknown>;
    return record.type === 'FeatureCollection' && Array.isArray(record.features);
};

const getFeatureCollectionFromRecord = (
    record: Record<string, unknown>
): GeoJSON.FeatureCollection | null => {
    if (isFeatureCollection(record)) {
        return record;
    }
    if (isFeatureCollection(record.featureCollection)) {
        return record.featureCollection;
    }
    if (isFeatureCollection(record.data)) {
        return record.data;
    }
    if (
        record.data &&
        typeof record.data === 'object' &&
        isFeatureCollection((record.data as Record<string, unknown>).featureCollection)
    ) {
        return (record.data as Record<string, unknown>).featureCollection as GeoJSON.FeatureCollection;
    }

    return null;
};

const safelyParseLambdaJson = (jsonString: string) => {
    try {
        return JSON.parse(jsonString);
    } catch {
        return jsonString;
    }
};

export const normalizeLambdaResponse = <T>(resp: T | string): T => {
    if (typeof resp !== 'string') {
        return resp;
    }

    return safelyParseLambdaJson(resp) as T;
};

const normalizeServicePath = (path: string): string => {
    if (/^https?:\/\//i.test(path)) {
        return path;
    }

    const cleanedPath = path.replace(/^\/+/, '');
    if (cleanedPath.startsWith('services/')) {
        return cleanedPath;
    }
    if (cleanedPath.startsWith('replancity_LambdaOperationService/')) {
        return `services/${cleanedPath}`;
    }

    return `${LAMBDA_SERVICE_PATH}/${cleanedPath}`;
};

const getOperationStartPath = (operation: LambdaOperation): string => {
    const endpoint =
        operation.startEndpoint ||
        operation.endpoint ||
        operation.actionUrl ||
        (typeof operation.type === 'string' ? operation.type : '');

    if (!endpoint) {
        throw new Error('Lambda operation does not include a start endpoint');
    }

    return normalizeServicePath(endpoint);
};

const getOperationMetadataPath = (operation: LambdaOperation): string | null => {
    const endpoint = operation.metadataEndpoint || operation.metadataUrl || '';

    return endpoint ? normalizeServicePath(endpoint) : null;
};

const getLambdaLayerPath = (item?: LambdaHistoryItem): string =>
    normalizeServicePath(item?.layerEndpoint || item?.layerUrl || 'getLambdaLayer');

const normalizeLambdaLayerResponse = (
    resp:
        | string
        | LambdaLayerDescriptor
        | LambdaLayerDescriptor[]
        | Record<string, unknown>
        | null
        | undefined
): LambdaLayerDescriptor[] => {
    const parsed = typeof resp === 'string' ? normalizeLambdaResponse<unknown>(resp) : resp;

    if (!parsed) {
        return [];
    }

    if (typeof parsed === 'string') {
        return [{ id: parsed } as LambdaLayerDescriptor];
    }

    if (Array.isArray(parsed)) {
        return parsed.filter(Boolean) as LambdaLayerDescriptor[];
    }

    if (typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const featureCollection = getFeatureCollectionFromRecord(record);
        if (featureCollection) {
            const layerRecord =
                record.layer && typeof record.layer === 'object'
                    ? (record.layer as Record<string, unknown>)
                    : record;

            return [
                {
                    ...(layerRecord as Partial<LambdaLayerDescriptor>),
                    featureCollection,
                    ...(record.legend ? { legend: record.legend } : {}),
                    ...(record.distribution ? { distribution: record.distribution } : {}),
                } as LambdaLayerDescriptor,
            ];
        }
        if (Array.isArray(record.layers)) {
            return record.layers.filter(Boolean) as LambdaLayerDescriptor[];
        }
        if (record.layer && typeof record.layer === 'object') {
            return [record.layer as LambdaLayerDescriptor];
        }
        if (record.id || record.entityName || record._entityName) {
            return [record as LambdaLayerDescriptor];
        }
    }

    return [];
};

const normalizeLambdaHistoryResponse = (
    resp:
        | string
        | LambdaHistoryItem
        | LambdaHistoryItem[]
        | Record<string, unknown>
        | null
        | undefined
): LambdaHistoryItem[] => {
    const parsed = typeof resp === 'string' ? normalizeLambdaResponse<unknown>(resp) : resp;

    if (!parsed) {
        return [];
    }

    if (Array.isArray(parsed)) {
        return parsed.filter(Boolean) as LambdaHistoryItem[];
    }

    if (typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        if (Array.isArray(record.items)) {
            return record.items.filter(Boolean) as LambdaHistoryItem[];
        }
        if (Array.isArray(record.data)) {
            return record.data.filter(Boolean) as LambdaHistoryItem[];
        }
        if (Array.isArray(record.result)) {
            return record.result.filter(Boolean) as LambdaHistoryItem[];
        }
        if (record.id) {
            return [record as LambdaHistoryItem];
        }
    }

    return [];
};

export const lambdaOperationsApi = {
    getAvailableOperations(abortSignal: AbortSignal): Promise<LambdaOperation[] | ApiError> {
        return restClient
            .fetch<string | LambdaOperation[]>({
                method: 'GET',
                path: `${LAMBDA_SERVICE_PATH}/listOperationsToRun`,
                fetchOptions: { signal: abortSignal },
            })
            .then(resp => normalizeLambdaResponse<LambdaOperation[]>(resp));
    },

    getOperationMetadata(
        operation: LambdaOperation,
        abortSignal: AbortSignal,
        presetId?: string
    ): Promise<LambdaOperationMetadata | MetadataPropertyEntity[] | ApiError> {
        const inlineMetadata = normalizeLambdaResponse<
            LambdaOperationMetadata | LambdaOperationField[] | undefined
        >(operation.metadata as any);

        if (inlineMetadata) {
            return Promise.resolve(
                Array.isArray(inlineMetadata) ? { fields: inlineMetadata } : inlineMetadata
            );
        }

        if (operation.metadataEntityName) {
            return restClient.fetch<MetadataPropertyEntity[]>({
                method: 'GET',
                path: `projects/metadata/${operation.metadataEntityName}`,
                data: {
                    ...(presetId ? { presetId } : {}),
                },
                fetchOptions: { signal: abortSignal },
            });
        }

        const metadataEndpoint = getOperationMetadataPath(operation);
        if (metadataEndpoint) {
            return restClient
                .fetch<string | LambdaOperationMetadata>({
                    method: 'GET',
                    path: metadataEndpoint,
                    data: { metadata: '' },
                    fetchOptions: { signal: abortSignal },
                })
                .then(resp => normalizeLambdaResponse<LambdaOperationMetadata>(resp));
        }

        return Promise.resolve({
            error: 'No metadata source',
            error_description: 'Lambda operation does not include metadata information',
            status: 400,
        });
    },

    startOperation(
        operation: LambdaOperation,
        payload: Record<string, unknown>,
        abortSignal: AbortSignal
    ): Promise<LambdaHistoryItem | ApiError | string> {
        const path = getOperationStartPath(operation);
        const data = { metadata: JSON.stringify(payload) } satisfies LambdaStartPayload;

        return restClient
            .fetch<string | LambdaHistoryItem>({
                method: 'POST',
                path,
                data,
                fetchOptions: { signal: abortSignal },
            })
            .then(resp => {
                const isServerErrorResponse =
                    resp &&
                    typeof resp === 'object' &&
                    'error' in resp &&
                    'status' in resp &&
                    typeof resp.status === 'number' &&
                    resp.status >= 500;

                if (isServerErrorResponse) {
                    return restClient
                        .fetch<string | LambdaHistoryItem>({
                            method: 'GET',
                            path,
                            data,
                            fetchOptions: { signal: abortSignal },
                        })
                        .then(getResp => normalizeLambdaResponse<LambdaHistoryItem | string>(getResp));
                }

                return normalizeLambdaResponse<LambdaHistoryItem | string>(resp);
            });
    },

    getCalculationHistory({
        presetId,
        abortSignal,
    }: {
        presetId: string;
        abortSignal: AbortSignal;
    }): Promise<LambdaHistoryItem[] | ApiError> {
        return restClient
            .fetch<string | LambdaHistoryItem | LambdaHistoryItem[] | Record<string, unknown>>({
                method: 'GET',
                path: `${LAMBDA_SERVICE_PATH}/list_replancity_LambdaOperation`,
                data: { presetId },
                fetchOptions: { signal: abortSignal },
            })
            .then(resp => normalizeLambdaHistoryResponse(resp));
    },

    restartCalculation({
        lambdaId,
        abortSignal,
    }: {
        lambdaId: string;
        abortSignal: AbortSignal;
    }): Promise<LambdaHistoryItem | ApiError | string> {
        return restClient
            .fetch<string | LambdaHistoryItem>({
                method: 'GET',
                path: `${LAMBDA_SERVICE_PATH}/restartLambdaOperation`,
                data: { lambdaId },
                fetchOptions: { signal: abortSignal },
            })
            .then(resp => normalizeLambdaResponse<LambdaHistoryItem | string>(resp));
    },

    getLambdaLayer({
        lambdaId,
        item,
        abortSignal,
    }: {
        lambdaId: string;
        item?: LambdaHistoryItem;
        abortSignal: AbortSignal;
    }): Promise<LambdaLayerDescriptor[] | ApiError> {
        return restClient
            .fetch<
                string | LambdaLayerDescriptor | LambdaLayerDescriptor[] | Record<string, unknown>
            >({
                method: 'GET',
                path: getLambdaLayerPath(item),
                data: { lambdaId },
                fetchOptions: { signal: abortSignal },
            })
            .then(resp => normalizeLambdaLayerResponse(resp));
    },
};
